import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HealthReportModal from '../Modal/HealthReportModal';
import styled from 'styled-components';

const parseFlexibleDate = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.split(/[ T]/)[0] || raw;
  const separator = dateOnly.includes("-")
    ? "-"
    : dateOnly.includes("/")
      ? "/"
      : null;

  if (separator) {
    const parts = dateOnly.split(separator);
    if (parts.length === 3) {
      let year;
      let month;
      let day;

      if (parts[0].length === 4) {
        year = Number(parts[0]);
        month = Number(parts[1]);
        day = Number(parts[2]);
      } else {
        day = Number(parts[0]);
        month = Number(parts[1]);
        year = Number(parts[2]);
      }

      if (year > 2400) {
        year -= 543;
      } else if (year < 100) {
        year += 2000;
      }

      const parsed = new Date(year, month - 1, day);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const calculateAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) return null;

  let age = examDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = examDate.getMonth() - birthDate.getMonth();
  const dayDiff = examDate.getDate() - birthDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 && age <= 150 ? age : null;
};

const normalizeGenderText = (gender) => {
  const value = String(gender || "").trim().toLowerCase();
  if (!value) return "-";
  if (["1", "m", "male", "ชาย"].includes(value)) return "ชาย";
  if (["2", "f", "female", "หญิง"].includes(value)) return "หญิง";
  return String(gender).trim();
};

const parseObjectValue = (value) => {
  if (!value) return {};

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const pickFirstArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }
  return [];
};

const buildBloodPressure = (sourceRecord = {}, healthData = {}) => {
  const systolic =
    healthData?.bps ||
    healthData?.sys ||
    sourceRecord?.bps ||
    sourceRecord?.sys ||
    sourceRecord?.systolic_bp ||
    healthData?.bloodPressure?.split?.("/")?.[0]?.trim();
  const diastolic =
    healthData?.bpd ||
    healthData?.dia ||
    sourceRecord?.bpd ||
    sourceRecord?.dia ||
    sourceRecord?.diastolic_bp ||
    healthData?.bloodPressure?.split?.("/")?.[1]?.trim();

  if (systolic && diastolic) {
    return {
      bloodPressure: `${systolic}/${diastolic}`,
      bps: systolic,
      bpd: diastolic,
    };
  }

  const existingBloodPressure =
    healthData?.bloodPressure ||
    sourceRecord?.bloodPressure ||
    sourceRecord?.sys_dias ||
    healthData?.["sys/dias"];

  if (existingBloodPressure) {
    const [sys, dia] = String(existingBloodPressure)
      .split("/")
      .map((part) => part.trim());
    return {
      bloodPressure: existingBloodPressure,
      bps: sys || "-",
      bpd: dia || "-",
    };
  }

  return { bloodPressure: "-", bps: "-", bpd: "-" };
};

const normalizePublicReportRecord = (record) => {
  const safeRecord = parseObjectValue(record);
  const healthData = parseObjectValue(safeRecord.health_data);
  const mergedParameters = pickFirstArray(
    healthData?.parameters,
    safeRecord?.sourceParameters,
    safeRecord?.parameters,
  );
  const mergedLabData = {
    ...parseObjectValue(safeRecord.labData),
    ...parseObjectValue(healthData?.labData),
  };
  const mergedHealthData = {
    ...healthData,
    parameters: mergedParameters,
    labData: mergedLabData,
  };
  const bloodPressureData = buildBloodPressure(safeRecord, mergedHealthData);

  return {
    ...safeRecord,
    health_data: mergedHealthData,
    parameters: mergedParameters,
    sourceParameters: mergedParameters,
    labData: mergedLabData,
    patientName:
      mergedHealthData?.patientName || safeRecord?.patientName || "-",
    age:
      calculateAgeAtDate(
        mergedHealthData?.birthday ||
          mergedHealthData?.birthdate ||
          mergedHealthData?.birth_date ||
          safeRecord?.birthday ||
          safeRecord?.birthdate ||
          safeRecord?.birth_date,
        mergedHealthData?.rawDate ||
          safeRecord?.rawDate ||
          safeRecord?.dateadd ||
          mergedHealthData?.date ||
          safeRecord?.date,
      ) ||
      mergedHealthData?.age ||
      safeRecord?.age ||
      "-",
    gender: normalizeGenderText(
      mergedHealthData?.gender || mergedHealthData?.sex || safeRecord?.gender,
    ),
    hn: mergedHealthData?.hn || safeRecord?.hn || "-",
    date: mergedHealthData?.date || safeRecord?.date || "-",
    rawDate:
      mergedHealthData?.rawDate || safeRecord?.rawDate || safeRecord?.dateadd || "",
    birthday:
      mergedHealthData?.birthday ||
      mergedHealthData?.birthdate ||
      mergedHealthData?.birth_date ||
      safeRecord?.birthday ||
      safeRecord?.birthdate ||
      safeRecord?.birth_date ||
      "",
    company:
      mergedHealthData?.company ||
      mergedHealthData?.campaign ||
      safeRecord?.company ||
      safeRecord?.campaign ||
      "-",
    diseases:
      mergedHealthData?.diseases || safeRecord?.diseases || "ไม่มี",
    foodAllergies:
      mergedHealthData?.foodAllergies ||
      safeRecord?.foodAllergies ||
      mergedLabData?.food_allergies ||
      mergedLabData?.["food allergies"] ||
      "-",
    weight: mergedHealthData?.weight || safeRecord?.weight || mergedLabData?.weight || "-",
    height: mergedHealthData?.height || safeRecord?.height || mergedLabData?.height || "-",
    bmi: mergedHealthData?.bmi || safeRecord?.bmi || mergedLabData?.bmi || "-",
    pulse: mergedHealthData?.pulse || safeRecord?.pulse || mergedLabData?.pulse || "-",
    ...bloodPressureData,
  };
};

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingMessage = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  font-size: 1.2rem;
  color: #1F2937;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00B5B8;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  font-size: 1.2rem;
  color: #DC2626;
  max-width: 500px;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ErrorDetail = styled.div`
  font-size: 0.9rem;
  color: #6B7280;
  margin-top: 1rem;
`;

const PublicHealthbook = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detect mobile view based on screen width
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fetchReport = async () => {
      if (!token) {
        setError('ไม่พบ Token ในลิงก์');
        setLoading(false);
        return;
      }

      try {
        // เรียก public API (ไม่ต้องส่ง Authorization header)
        const apiUrl = process.env.REACT_APP_API_URL ||
          (window.location.hostname === 'localhost'
            ? 'http://localhost:8000'
            : 'https://mfuwellnesscenter-server.dietz.asia');
        const response = await fetch(
          `${apiUrl}/api/healthbook/public?token=${token}`
        );

        if (!response.ok) {
          let errorMessage = 'ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            // response body ว่างหรือไม่ใช่ JSON
          }
          throw new Error(errorMessage);
        }

        let result;
        try {
          result = await response.json();
        } catch {
          throw new Error('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
        }

        // Backend ส่งมาแบบ: { status: "success", message: { result: true, data: {...} } }
        const actualData = result.message || result;

        if (!actualData.result || !actualData.data) {
          throw new Error('ไม่พบข้อมูลสุขภาพ');
        }

        // Log เพื่อ debug
        console.log('📦 Received data:', actualData.data);

        // Backend ส่งมาพร้อม parameters แล้ว ไม่ต้อง transform
        const reportData = normalizePublicReportRecord(actualData.data);

        console.log('✅ Report data with parameters:', reportData);
        setReportData(reportData);
      } catch (err) {
        console.error('Error fetching public health report:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [token]);

  // Listen to window resize to update mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>
          <Spinner />
          <div>กำลังโหลดข้อมูลสุขภาพ...</div>
        </LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>{error}</ErrorTitle>
          <ErrorDetail>
            กรุณาตรวจสอบลิงก์หรือติดต่อผู้ดูแลระบบหากพบปัญหา
          </ErrorDetail>
        </ErrorMessage>
      </Container>
    );
  }

  if (!reportData) {
    console.log('❌ reportData is null/undefined');
    return (
      <Container>
        <ErrorMessage>
          <ErrorIcon>❌</ErrorIcon>
          <ErrorTitle>ไม่พบข้อมูล</ErrorTitle>
        </ErrorMessage>
      </Container>
    );
  }

  console.log('🎯 Rendering HealthReportModal with recordData:', reportData);

  // HealthReportModal คาดหวัง array ไม่ใช่ object เดี่ยว
  // ต้องห่อด้วย array
  const recordDataArray = Array.isArray(reportData) ? reportData : [reportData];

  return (
    <HealthReportModal
      isOpen={true}
      onClose={() => window.close()}
      recordData={recordDataArray}
      disableAutoPrint={true}
      isMobileView={isMobileView}
      isPublicView={true}
    />
  );
};

export default PublicHealthbook;
